/**
 * Bumper stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const bumperStickersPack: ToilePack = {
    slug: 'bumper-stickers',
    category: 'sticker',
    name: 'Bumper stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'slogan', label: 'Slogan', subject: 'A wide rectangle lettered "HONK IF YOU\'RE HAPPY" in bold'},
        {key: 'park', label: 'Park badge', subject: 'A wide oval badge with a mountain and a lake, lettered "EXPLORE MORE"'},
        {key: 'band', label: 'Band', subject: 'A wide band lettered "DRIVE KIND" in a rounded script'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['swap in a pet slogan', 'as a set of four', 'in neon colours'],
    directionHint: 'try: swap in a pet slogan',
    seo: {
        title: 'Bumper Stickers: Slogan, National Park, Coexist Band',
        metaDescription: 'Nine bumper stickers — a lettered slogan, a national park badge, a coexist-style band — in retro, bold, and minimal styles.',
        h1: 'Bumper stickers: slogan, national park badge, coexist band in three styles',
        tagline: 'Nine bumper stickers, three designs, three styles each.',
        intro: 'The wide sticker for the bumper: a lettered slogan, a national park badge, a lettered coexist-style band of symbols. Rendered retro, bold, and minimal to match the look of stickers already on the road. Ask for your own slogan or a specific park name, and the same three styles carry it.',
        useCases: [
            'a car bumper or rear window sticker',
            'a water bottle or laptop slogan sticker',
            'a national park badge for a road trip car',
            'a gift for a hiker or road-tripper',
            'a printable bumper sticker design',
        ],
        faq: [
            {q: 'Can I get my own slogan or a specific park name?', a: 'Yes — the direction field takes any slogan or place name, rendered in the same three styles.'},
            {q: 'Will these hold up outdoors on a car bumper?', a: 'These are print-at-home designs; for real outdoor durability on a vehicle, print onto weatherproof vinyl sticker stock rated for outdoor use.'},
            {q: "What's the difference between retro, bold, and minimal?", a: "Retro uses a vintage badge look, bold is a thick high-contrast sans-serif, and minimal is a clean single-weight line — pick whichever matches your car's other stickers."},
            {q: 'Is the coexist-style band using real religious symbols?', a: "It's a lettered band in that familiar visual format — check the specific symbol set generated before printing if that detail matters to you."},
            {q: 'Can I print these to sell at a market or shop?', a: 'Personal use is fine; check the license terms before reselling the designs themselves.'},
        ],
    },
}
